import type { GridColDef } from '@mui/x-data-grid-pro';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import { MoreVert } from '@mui/icons-material';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import CompanyLogo from '@shared/components/CompanyLogo';
import { ModernDataTable } from '@shared/components/DataTable';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';

type ChipColor = 'success' | 'warning' | 'info' | 'error' | 'default' | 'primary';

interface StatusConfig {
  label: string;
  color?: ChipColor;
  customStyles?: { color?: string; bgcolor?: string };
}

// Status → chip presentation. A status with `customStyles` renders those
// bespoke colours and no MUI palette colour; the rest map to a palette colour.
// Unknown statuses fall back to a title-cased label with the default colour.
const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: { label: 'Active', color: 'primary' },
  accepted_by_review_author: {
    label: 'Accepted',
    customStyles: { bgcolor: '#4caf50', color: '#fff' },
  },
  pending: { label: 'Pending', color: 'warning' },
  fulfilled: {
    label: 'Fulfilled',
    customStyles: { bgcolor: '#2e7d32', color: '#fff' },
  },
  cancelled: { label: 'Cancelled', color: 'error' },
  rejected: { label: 'Declined', color: 'error' },
  expired: { label: 'Expired', color: 'error' },
  paused: { label: 'Paused', color: 'default' },
};

interface ConsultationRequestsTableProps {
  consultationRequests: IntelligenceRequest[];
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, requestId: number) => void;
  title?: string;
  subtitle?: string;
}

export const ConsultationRequestsTable: React.FC<ConsultationRequestsTableProps> = ({
  consultationRequests,
  onMenuOpen,
  title = 'Consultation Requests',
  subtitle = 'Manage your incoming consultation requests (only visible to you)',
}) => {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'DATE REQUESTED',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => moment(params.value).format('MM/DD/YYYY'),
    },
    {
      field: 'reviewId',
      headerName: 'REVIEW',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => {
        const row = params.row as IntelligenceRequest;
        const reviewId = row.reviewId;
        const companyName = row.companyName || row.company?.name;
        const companyId = row.companyId || row.company?.id;

        if (!reviewId) {
          return (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/review-reader/${encodeURIComponent(companyName || 'company')}/${reviewId}`);
            }}
          >
            <CompanyLogo
              companyId={companyId}
              companyName={companyName || ''}
              alt={companyName || 'Company'}
              sx={{ width: 28, height: 28 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: '#0b74da',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {companyName || 'View Review'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'amountUsd',
      headerName: 'AMOUNT',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const amount = params.value as number | undefined;
        if (amount === undefined || amount === null) return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount);
      },
    },
    {
      field: 'engagementType',
      headerName: 'ENGAGEMENT TYPE',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const type = params.value as string | undefined;
        if (!type) return '-';
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
    },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const status = (params.value as string)?.toLowerCase() || '';
        const config = STATUS_CONFIG[status];

        const customStyles = config?.customStyles;
        const label = config?.label ?? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        // A bespoke style wins over the MUI palette colour, matching the
        // previous behaviour where custom sx replaced the chip colour.
        const color = customStyles ? undefined : (config?.color ?? 'default');

        return <Chip label={label} color={color} size="small" sx={{ fontWeight: 600, ...customStyles }} />;
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          onClick={(e) => onMenuOpen(e, params.row.id)}
          size="small"
          sx={{
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: '#0b74da',
              backgroundColor: 'rgba(120, 187, 241, 0.14)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <ModernDataTable
      title={title}
      subtitle={subtitle}
      rows={consultationRequests}
      columns={columns}
      initialPageSize={10}
      pageSizeOptions={[5, 10, 25]}
      autoHeight
      hideFooter
    />
  );
};
